/**
 * useServerPagedSearch — ricerca server-side paginata con load-more.
 *
 * Incapsula il pattern ricorrente di picker/autocomplete "search + scroll":
 *   - L'utente digita → debounce N ms → fetch page 1 → lista mostra primi K.
 *   - L'utente scrolla in fondo alla lista → fetch page+1 → append items.
 *   - L'utente cambia query → reset page=1 + svuota items → fetch.
 *
 * Usato da AddRelationDrawer, TaskForm/TaskEditDrawer (picker di record
 * associabili), QuotationDetail (lookup business), ecc.
 *
 * L'hook e' agnostico rispetto alla source: riceve una `fetchPage` che
 * prende `(query, page, pageSize)` e ritorna `{items, totalCount}`. Il
 * chiamante la costruisce tipicamente chiudendo sopra un'api entity +
 * groupId + altri filtri.
 *
 * Guardie anti-race: ogni fetch ha un requestId incrementale; se una
 * response arriva dopo che l'utente ha gia' cambiato query o triggerato
 * il successivo load-more, viene scartata.
 *
 * Esempio d'uso (AddRelationDrawer):
 * ```tsx
 * const { items, totalCount, loading, query, setQuery, loadMore, hasMore } =
 *   useServerPagedSearch({
 *     fetchPage: (query, page, pageSize) =>
 *       entityType.search(query, groupId, { page, pageSize }),
 *     pageSize: 20,
 *     debounceMs: 300,
 *   });
 *
 * <input value={query} onChange={(e) => setQuery(e.target.value)} />
 * <ul onScroll={(e) => {
 *   const el = e.currentTarget;
 *   if (hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 50) loadMore();
 * }}>...</ul>
 * ```
 */

import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react';
import { useDebounce } from './useDebounce';

export interface PageFetchResult<T> {
  items: T[];
  totalCount: number;
}

/** Shape dei reason di MUI Autocomplete onInputChange. Duplicata qui per
 *  tenere @crm/shared UI-library-agnostic (no import @mui/material). */
type AutocompleteReason = 'input' | 'reset' | 'clear' | 'selectOption' | 'removeOption' | 'blur';

/** Props pronti per MUI Autocomplete server-side paginato. Applica le 4
 *  regole co-dipendenti documentate in useAsyncAutocomplete. */
export interface AsyncAutocompleteProps<T> {
  options: T[];
  inputValue: string;
  filterOptions: (items: T[]) => T[];
  onInputChange: (event: unknown, newValue: string, reason: AutocompleteReason) => void;
}

/** Props per `slotProps.listbox` dell'Autocomplete: handler onScroll che
 *  triggera loadMore quando l'utente si avvicina al bottom del dropdown. */
export interface AsyncAutocompleteListboxProps {
  onScroll: (e: UIEvent<HTMLUListElement>) => void;
}

/** Distanza (px) dal bottom del listbox a cui scatta il load-more. */
const LISTBOX_SCROLL_THRESHOLD = 50;

interface UseServerPagedSearchOptions<T> {
  /** Funzione fetch: riceve (query, page, pageSize), ritorna items + totalCount. */
  fetchPage: (query: string, page: number, pageSize: number) => Promise<PageFetchResult<T>>;
  /** Dimensione pagina (default 20). */
  pageSize?: number;
  /** Debounce sulla query dopo cui triggera il primo fetch (default 300ms). */
  debounceMs?: number;
  /**
   * Dipendenze esterne che se cambiano invalidano la ricerca in corso
   * (es. groupId che cambia a logout). Array di valori primitivi.
   */
  resetOn?: ReadonlyArray<unknown>;
  /**
   * Se false, disabilita la ricerca (la lista resta vuota e non parte
   * nessuna fetch). Utile per pre-condizioni come groupId > 0.
   */
  enabled?: boolean;
}

interface UseServerPagedSearchReturn<T> {
  /** Items accumulati (page 1 + page 2 + ... dopo load-more). */
  items: T[];
  /** Totale lato server della query corrente. */
  totalCount: number;
  /** true durante la fetch (sia primo che load-more). */
  loading: boolean;
  /** Query grezza digitata dall'utente (non debounced). */
  query: string;
  /** Setter per la query: da chiamare onChange dell'input. */
  setQuery: (q: string) => void;
  /**
   * Chiedi la prossima pagina. Safe to call piu' volte: se loading o
   * hasMore=false, no-op. Da chiamare tipicamente nel onScroll del
   * container della lista quando lo scroll raggiunge il bottom.
   */
  loadMore: () => void;
  /** true se items.length < totalCount (ci sono altre pagine). */
  hasMore: boolean;
  /**
   * Bundle di props pronti per un MUI Autocomplete async paginato.
   * Spread-are sull'<Autocomplete /> e passare `slotProps={{ listbox: listboxProps }}`
   * per il load-more on-scroll. Vedi useAsyncAutocomplete per il pattern
   * delle 4 regole async co-dipendenti.
   */
  autocompleteProps: AsyncAutocompleteProps<T>;
  /** slotProps.listbox: handler onScroll che chiama loadMore vicino al bottom. */
  listboxProps: AsyncAutocompleteListboxProps;
}

export function useServerPagedSearch<T>(
  options: UseServerPagedSearchOptions<T>
): UseServerPagedSearchReturn<T> {
  const {
    fetchPage,
    pageSize = 20,
    debounceMs = 300,
    resetOn = [],
    enabled = true,
  } = options;

  const [query, setQuery] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // `debouncedQuery` e' la query "consolidata" con cui lanciamo la
  // fetch. La query grezza continua a seguire il typing per l'UX.
  const debouncedQuery = useDebounce(query, debounceMs);

  // Guardia anti-race: incrementa ad ogni richiesta; solo la risposta
  // col requestId corrente viene applicata. Evita che una fetch vecchia
  // (query precedente o pagina precedente) sovrascriva una piu' recente.
  const requestIdRef = useRef(0);

  // true mentre e' in volo la fetch di reset (pagina 1 dopo un cambio di
  // query/resetOn). Blocca `loadMore`, che altrimenti la scavalcherebbe:
  // requestIdRef da solo tratta reset e load-more come intercambiabili,
  // e vince l'ultimo partito anche quando e' il meno autorevole.
  //
  // Il caso reale: al cambio di resetOn cambia `fetchPage`, quindi cambia
  // `loadMore`, quindi ri-scatta l'effect di useScrollAutoFill — che legge
  // `loading`/`hasMore` dalla closure del render, dove valgono ancora i
  // valori pre-reset. Se nel frattempo la lista renderizzata si e'
  // accorciata (es. un filtro client-side del chiamante), l'autofill vede
  // un container non scrollabile e chiama loadMore: rid avanza e la
  // risposta della pagina 1 viene scartata, lasciando la lista vecchia.
  //
  // Ref e non state di proposito: va letta al momento della chiamata, non
  // congelata nella closure del render (e' esattamente il difetto che
  // rende possibile la race).
  const resetInFlightRef = useRef(false);

  // Reset dello stato quando la query debounced cambia o quando
  // cambiano le deps esterne (es. groupId).
  // Usiamo JSON.stringify(resetOn) come proxy stabile per l'array:
  // deps di tipo array vanno trattate come atom nei useEffect.
  const resetKey = JSON.stringify(resetOn);
  useEffect(() => {
    if (!enabled) {
      // Niente fetch in volo da proteggere: sbloccare qui evita che la ref
      // resti alzata se `enabled` cade mentre un reset era in corso.
      resetInFlightRef.current = false;
      setItems([]);
      setTotalCount(0);
      setPage(1);
      return;
    }
    let cancelled = false;
    const rid = ++requestIdRef.current;
    resetInFlightRef.current = true;
    setLoading(true);
    fetchPage(debouncedQuery, 1, pageSize)
      .then((result) => {
        if (cancelled || rid !== requestIdRef.current) return;
        setItems(result.items);
        setTotalCount(result.totalCount);
        setPage(1);
      })
      .catch(() => {
        if (cancelled || rid !== requestIdRef.current) return;
        setItems([]);
        setTotalCount(0);
        setPage(1);
      })
      .finally(() => {
        // Solo la richiesta ancora corrente abbassa la ref: se un reset
        // piu' recente e' subentrato, tocca a lui sbloccare.
        if (cancelled || rid !== requestIdRef.current) return;
        resetInFlightRef.current = false;
        setLoading(false);
      });
    return () => { cancelled = true; };
    // fetchPage intenzionalmente escluso dalle deps: sarebbe una nuova
    // closure ad ogni render del chiamante. I changes alle dipendenze
    // di fetchPage vanno passati via resetOn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, pageSize, enabled, resetKey]);

  const hasMore = items.length < totalCount;

  const loadMore = useCallback(() => {
    if (!enabled || loading || !hasMore) return;
    // Letta a runtime: `loading` qui sopra e' il valore del render, che
    // durante un reset appena partito e' ancora `false`. Senza questa
    // riga il load-more scavalcherebbe la pagina 1 del reset.
    if (resetInFlightRef.current) return;
    const nextPage = page + 1;
    const rid = ++requestIdRef.current;
    setLoading(true);
    fetchPage(debouncedQuery, nextPage, pageSize)
      .then((result) => {
        if (rid !== requestIdRef.current) return;
        setItems((prev) => [...prev, ...result.items]);
        setTotalCount(result.totalCount);
        setPage(nextPage);
      })
      .catch(() => {
        // silent: la pagina precedente resta visibile, utente puo' ritentare.
      })
      .finally(() => {
        if (rid !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [debouncedQuery, enabled, fetchPage, hasMore, loading, page, pageSize]);

  const autocompleteProps: AsyncAutocompleteProps<T> = {
    options: items,
    inputValue: query,
    filterOptions: (x: T[]) => x,
    onInputChange: (_event, newValue, reason) => {
      // Vedi useAsyncAutocomplete: ignoriamo SOLO 'reset' (sincronia
      // interna MUI post-fetch che sovrascriverebbe la query); tutte le
      // altre transizioni (input, selectOption, clear, blur, removeOption)
      // sono guidate dall'utente e vanno applicate.
      if (reason === 'reset') return;
      setQuery(newValue);
    },
  };

  const listboxProps: AsyncAutocompleteListboxProps = {
    onScroll: (e) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < LISTBOX_SCROLL_THRESHOLD) {
        loadMore();
      }
    },
  };

  return { items, totalCount, loading, query, setQuery, loadMore, hasMore, autocompleteProps, listboxProps };
}
